/**
 * Phase 17.7 UX hotfix — Unified Campaign cross-validation source of truth.
 *
 * Mirrors every Pydantic validator in `backend/models/unified_campaign.py`
 * so the UI can enforce constraints BEFORE submit, preventing operators
 * from hitting a backend 422 that the UI could have prevented.
 *
 * Public surface:
 *   • UnifiedCampaignSchema — Zod schema of the full submit payload.
 *   • FieldBounds           — numeric ranges + step hints per input.
 *   • validateCampaign(state) → { errors[], warnings[], autoFixHints[] }
 *
 * Two-tier output:
 *   • errors[]   — hard rules; submit must be blocked.
 *   • warnings[] — soft hints (e.g. n_samples > 1000); submit allowed.
 *   • autoFixHints[] — informational strings explaining UI-side auto-flips
 *                      (e.g. "Switched to GA sampling because
 *                      guidance gain search was ticked"). The builder
 *                      page applies these flips synchronously in its
 *                      controlled handlers; this array exists for the
 *                      "what was changed" inline note.
 */
import { z } from "zod";

// ---------------------------------------------------------------------------
// Field bounds — every Field(..., ge, le, min_length, max_length) in the
// backend mirrored here. Bounds are used both for the Zod schema AND to
// drive `min` / `max` / `step` attrs on the corresponding <Input> elements.
// ---------------------------------------------------------------------------
export const FieldBounds = {
    name: { min: 1, max: 160 },
    description: { min: 0, max: 4000 },
    n_samples: { min: 2, max: 10000, softWarnOver: 1000 },
    mc_seeds_per_sample: { min: 1, max: 100 },
    seed: { min: 0 },
    ga_population_size: { min: 8, max: 128 },
    ga_generations: { min: 2, max: 50 },
    ga_mc_seeds_per_individual: { min: 1, max: 100 },
    ga_max_workers: { min: 1, max: 16 },
    ga_tournament_size: { min: 2, max: 8 },
    ga_crossover_rate: { min: 0, max: 1, step: 0.05 },
    ga_mutation_rate: { min: 0, max: 1, step: 0.05 },
    ga_elitism: { min: 0, max: 10 },
    ga_early_stop_no_improve: { min: 0, max: 20 },
    sensitivity_samples_per_variable: { min: 2, max: 200 },
    target_pk_threshold: { min: 0.01, max: 0.99, step: 0.05 },
    target_pk_max_iterations: { min: 1, max: 100 },
    target_pk_samples_per_iteration: { min: 1, max: 200 },
    target_pk_lambda_penalty: { min: 0, max: 10, step: 0.05 },
    confidence_alpha: { min: 0.001, max: 0.5, step: 0.005 },
    envelope_strata_per_dim: { min: 2, max: 20 },
    envelope_levels_per_dim: { min: 2, max: 10 },
    envelope_samples_per_point: { min: 1, max: 100 },
};

// ---------------------------------------------------------------------------
// Zod schema (declarative mirror — used by the RTL test + future integrations)
// ---------------------------------------------------------------------------
const ICRangeSchema = z.object({
    lo: z.number(),
    hi: z.number(),
}).refine((d) => d.lo <= d.hi, {
    message: "Range minimum must be ≤ maximum",
});

const ICBoundsSchema = z.object({
    x_m: ICRangeSchema.nullable().optional(),
    y_m: ICRangeSchema.nullable().optional(),
    z_m: ICRangeSchema.nullable().optional(),
}).nullable();

const HeadingSchema = z.object({
    mode: z.enum(["fixed", "range"]),
    value_deg: z.number().nullable().optional(),
    lo_deg: z.number().nullable().optional(),
    hi_deg: z.number().nullable().optional(),
}).refine((d) => d.mode !== "fixed" || d.value_deg != null, {
    message: "Heading 'fixed' mode requires value_deg",
}).refine((d) => d.mode !== "range" || (d.lo_deg != null && d.hi_deg != null), {
    message: "Heading 'range' mode requires lo_deg + hi_deg",
}).refine((d) => d.mode !== "range" || (d.lo_deg ?? 0) <= (d.hi_deg ?? 0), {
    message: "Heading lo_deg must be ≤ hi_deg",
}).nullable();

const PolarRangeSchema = z.object({
    lo: z.number(),
    hi: z.number(),
}).refine((d) => d.lo <= d.hi, {
    message: "Polar range lo must be ≤ hi",
});

export const UnifiedCampaignSchema = z.object({
    name: z.string()
        .min(FieldBounds.name.min, "Campaign name is required")
        .max(FieldBounds.name.max),
    description: z.string().max(FieldBounds.description.max),
    scenario_id: z.string().min(1, "Scenario is required"),
    variance_dimensions: z.object({
        target_ic_bounds: ICBoundsSchema,
        interceptor_ic_bounds: ICBoundsSchema,
        target_heading: HeadingSchema,
        target_control_pattern: z.array(z.enum(["straight", "weave", "jink"])),
        sensor_degradation: z.boolean(),
        fuel_margin: z.boolean(),
        aero_noise: z.boolean(),
        guidance_gain_search: z.boolean(),
        perturbation_overrides: z.record(z.any()),
        perturbation_preset: z.enum(["nominal", "stressing", "worst_case", "custom"]),
        geometry_envelope: z.object({
            range_m: PolarRangeSchema.nullable().optional(),
            bearing_deg: PolarRangeSchema.nullable().optional(),
            altitude_m: PolarRangeSchema.nullable().optional(),
            approach_vector_deg: PolarRangeSchema.nullable().optional(),
            sampler_kind: z.enum(["uniform", "stratified", "lhs", "grid"]),
            samples_per_point: z.number().int().min(FieldBounds.envelope_samples_per_point.min)
                .max(FieldBounds.envelope_samples_per_point.max),
        }).nullable(),
        degradation_profile_id: z.string().nullable(),
    }),
    sampling_strategy: z.object({
        kind: z.enum(["random", "lhs", "stratified", "ga"]),
        n_samples: z.number().int().min(FieldBounds.n_samples.min).max(FieldBounds.n_samples.max),
        mc_seeds_per_sample: z.number().int().min(FieldBounds.mc_seeds_per_sample.min)
            .max(FieldBounds.mc_seeds_per_sample.max),
        seed: z.number().int().min(FieldBounds.seed.min),
        ga_population_size: z.number().int().min(FieldBounds.ga_population_size.min)
            .max(FieldBounds.ga_population_size.max),
        ga_generations: z.number().int().min(FieldBounds.ga_generations.min)
            .max(FieldBounds.ga_generations.max),
        ga_mc_seeds_per_individual: z.number().int().min(FieldBounds.ga_mc_seeds_per_individual.min)
            .max(FieldBounds.ga_mc_seeds_per_individual.max),
        ga_max_workers: z.number().int().min(FieldBounds.ga_max_workers.min)
            .max(FieldBounds.ga_max_workers.max),
        ga_tournament_size: z.number().int().min(FieldBounds.ga_tournament_size.min)
            .max(FieldBounds.ga_tournament_size.max),
        ga_crossover_rate: z.number().min(FieldBounds.ga_crossover_rate.min)
            .max(FieldBounds.ga_crossover_rate.max),
        ga_mutation_rate: z.number().min(FieldBounds.ga_mutation_rate.min)
            .max(FieldBounds.ga_mutation_rate.max),
        ga_elitism: z.number().int().min(FieldBounds.ga_elitism.min)
            .max(FieldBounds.ga_elitism.max),
        ga_early_stop_no_improve: z.number().int().min(FieldBounds.ga_early_stop_no_improve.min)
            .max(FieldBounds.ga_early_stop_no_improve.max),
        sensitivity_method: z.enum(["variance_first_order", "one_at_a_time"]),
        sensitivity_samples_per_variable: z.number().int()
            .min(FieldBounds.sensitivity_samples_per_variable.min)
            .max(FieldBounds.sensitivity_samples_per_variable.max),
        target_pk_threshold: z.number().min(FieldBounds.target_pk_threshold.min)
            .max(FieldBounds.target_pk_threshold.max),
        target_pk_max_iterations: z.number().int().min(FieldBounds.target_pk_max_iterations.min)
            .max(FieldBounds.target_pk_max_iterations.max),
        target_pk_samples_per_iteration: z.number().int()
            .min(FieldBounds.target_pk_samples_per_iteration.min)
            .max(FieldBounds.target_pk_samples_per_iteration.max),
        target_pk_lambda_penalty: z.number().min(FieldBounds.target_pk_lambda_penalty.min)
            .max(FieldBounds.target_pk_lambda_penalty.max),
        confidence_alpha: z.number().min(FieldBounds.confidence_alpha.min)
            .max(FieldBounds.confidence_alpha.max),
    }),
    analysis_goals: z.object({
        descriptive_pk: z.boolean(),
        sensitivity_ranking: z.boolean(),
        target_pk_search: z.boolean(),
        envelope_heatmap: z.boolean(),
        ga_optimisation_convergence: z.boolean(),
        adaptive_policy_training: z.boolean(),
    }),
}).superRefine((d, ctx) => {
    // Cross-field rules — exact mirror of UnifiedCampaignCreate._cross_validate.
    const gs = d.variance_dimensions.guidance_gain_search;
    const sk = d.sampling_strategy.kind;
    const ga_goal = d.analysis_goals.ga_optimisation_convergence;
    if (gs && sk !== "ga") {
        ctx.addIssue({ code: z.ZodIssueCode.custom,
            path: ["sampling_strategy", "kind"],
            message: "guidance_gain_search=true requires sampling strategy = GA" });
    }
    if (sk === "ga" && !gs) {
        ctx.addIssue({ code: z.ZodIssueCode.custom,
            path: ["variance_dimensions", "guidance_gain_search"],
            message: "sampling strategy = GA requires guidance_gain_search=true" });
    }
    if (ga_goal && sk !== "ga") {
        ctx.addIssue({ code: z.ZodIssueCode.custom,
            path: ["analysis_goals", "ga_optimisation_convergence"],
            message: "GA convergence goal requires sampling strategy = GA" });
    }
});

// ---------------------------------------------------------------------------
// Variance-dimension helpers
// ---------------------------------------------------------------------------
function isIcAxisActive(rng) {
    return rng && Number(rng.lo) !== Number(rng.hi);
}
function isIcBlockActive(block) {
    if (!block) return false;
    return ["x_m", "y_m", "z_m"].some((ax) => isIcAxisActive(block[ax]));
}
function isEnvelopeActive(env) {
    if (!env) return false;
    const rng = env.range_m;
    return !!rng && Number(rng.lo) !== Number(rng.hi);
}

function countActiveVarianceDimensions(vd) {
    let n = 0;
    if (isIcBlockActive(vd.target_ic_bounds)) n++;
    if (isIcBlockActive(vd.interceptor_ic_bounds)) n++;
    if (vd.target_heading && vd.target_heading.mode === "range") n++;
    if ((vd.target_control_pattern || []).length > 1) n++;
    if (vd.sensor_degradation) n++;
    if (vd.fuel_margin) n++;
    if (vd.aero_noise) n++;
    if (vd.guidance_gain_search) n++;
    if (isEnvelopeActive(vd.geometry_envelope)) n++;
    if (vd.degradation_profile_id) n++;
    return n;
}

// ---------------------------------------------------------------------------
// Cross-field validator — produces a UI-ready bucket of errors / warnings.
// ---------------------------------------------------------------------------
export function validateCampaign(state) {
    const errors = [];
    const warnings = [];
    const autoFixHints = [];

    // Required fields
    if (!state.name?.trim()) {
        errors.push({ id: "name-required", field: "name",
            message: "Campaign name is required" });
    } else if (state.name.length > FieldBounds.name.max) {
        errors.push({ id: "name-too-long", field: "name",
            message: `Campaign name must be ≤ ${FieldBounds.name.max} characters` });
    }
    if (!state.scenario_id) {
        errors.push({ id: "scenario-required", field: "scenario",
            message: "Scenario is required" });
    }

    // n_samples bounds + soft warning
    const n = Number(state.sampling_strategy?.n_samples ?? 0);
    if (!Number.isFinite(n) || n < FieldBounds.n_samples.min || n > FieldBounds.n_samples.max) {
        errors.push({ id: "n-samples-bounds", field: "n_samples",
            message: `n_samples must be between ${FieldBounds.n_samples.min} and ${FieldBounds.n_samples.max}` });
    } else if (state.sampling_strategy?.kind !== "ga" && n > FieldBounds.n_samples.softWarnOver) {
        warnings.push({ id: "n-samples-soft", field: "n_samples",
            message: `n_samples = ${n} — wallclock may exceed 5 min; consider reducing or splitting` });
    }

    // mc_seeds_per_sample
    const mc = Number(state.sampling_strategy?.mc_seeds_per_sample ?? 1);
    if (mc < FieldBounds.mc_seeds_per_sample.min || mc > FieldBounds.mc_seeds_per_sample.max) {
        errors.push({ id: "mc-seeds-bounds", field: "mc_seeds_per_sample",
            message: `mc_seeds_per_sample must be between ${FieldBounds.mc_seeds_per_sample.min} and ${FieldBounds.mc_seeds_per_sample.max}` });
    }

    // ---- Cross-field rules (mirror of UnifiedCampaignCreate._cross_validate) ----
    const vd = state.variance_dimensions || {};
    const ss = state.sampling_strategy || {};
    const ag = state.analysis_goals || {};
    if (vd.guidance_gain_search && ss.kind !== "ga") {
        errors.push({ id: "ggs-requires-ga", field: "sampling_kind",
            message: "Guidance gain search requires sampling strategy = GA" });
    }
    if (ss.kind === "ga" && !vd.guidance_gain_search) {
        errors.push({ id: "ga-requires-ggs", field: "guidance_gain_search",
            message: "Sampling strategy = GA requires guidance gain search enabled" });
    }
    if (ag.ga_optimisation_convergence && ss.kind !== "ga") {
        errors.push({ id: "ga-goal-requires-ga", field: "ga_optimisation_convergence",
            message: "Goal 'GA optimisation convergence' requires sampling strategy = GA" });
    }

    // ---- Analysis-goal soundness rules (PM-requested, beyond backend) ----
    const nDims = countActiveVarianceDimensions(vd);
    if (ag.sensitivity_ranking && nDims < 1) {
        errors.push({ id: "sens-needs-dim", field: "sensitivity_ranking",
            message: "Sensitivity ranking needs at least 1 active variance dimension to be meaningful" });
    } else if (ag.sensitivity_ranking && nDims < 3) {
        warnings.push({ id: "sens-soft-3dim", field: "sensitivity_ranking",
            message: `Sensitivity ranking is most informative with ≥3 active variance dimensions (currently ${nDims})` });
    }
    if (ag.envelope_heatmap && !isEnvelopeActive(vd.geometry_envelope) && !isIcBlockActive(vd.target_ic_bounds)) {
        errors.push({ id: "heatmap-needs-geo", field: "envelope_heatmap",
            message: "Envelope heatmap needs either a polar envelope OR target IC bounds active" });
    }
    if (ag.target_pk_search) {
        const t = Number(ss.target_pk_threshold ?? 0.8);
        if (!(t > FieldBounds.target_pk_threshold.min && t < FieldBounds.target_pk_threshold.max)) {
            errors.push({ id: "tpk-threshold-bounds", field: "target_pk_threshold",
                message: `Target Pk threshold must be between ${FieldBounds.target_pk_threshold.min} and ${FieldBounds.target_pk_threshold.max} (exclusive)` });
        }
        if (nDims < 1) {
            errors.push({ id: "tpk-needs-dim", field: "target_pk_search",
                message: "Target Pk search needs at least 1 active variance dimension to search across" });
        }
    }
    if (ag.adaptive_policy_training) {
        errors.push({ id: "adaptive-deferred", field: "adaptive_policy_training",
            message: "Adaptive policy training is on the product roadmap and not yet available — uncheck to submit" });
    }
    if (!ag.descriptive_pk && !ag.sensitivity_ranking && !ag.target_pk_search
            && !ag.envelope_heatmap && !ag.ga_optimisation_convergence) {
        errors.push({ id: "goal-required", field: "analysis_goals",
            message: "At least one analysis goal must be selected" });
    }

    // ---- IC-range / polar-range / heading-range integrity ----
    const checkIcBlock = (block, label) => {
        if (!block) return;
        for (const ax of ["x_m", "y_m", "z_m"]) {
            const r = block[ax];
            if (!r) continue;
            if (Number(r.lo) > Number(r.hi)) {
                errors.push({ id: `ic-${label}-${ax}-inverted`, field: `${label}.${ax}`,
                    message: `${label} ${ax}: minimum (${r.lo}) must be ≤ maximum (${r.hi})` });
            }
        }
    };
    checkIcBlock(vd.target_ic_bounds, "target_ic_bounds");
    checkIcBlock(vd.interceptor_ic_bounds, "interceptor_ic_bounds");

    if (vd.target_heading) {
        const h = vd.target_heading;
        if (h.mode === "fixed" && (h.value_deg == null || Number.isNaN(Number(h.value_deg)))) {
            errors.push({ id: "heading-fixed-value", field: "target_heading.value_deg",
                message: "Target heading: 'fixed' mode requires a value (deg)" });
        }
        if (h.mode === "range") {
            if (h.lo_deg == null || h.hi_deg == null) {
                errors.push({ id: "heading-range-bounds", field: "target_heading",
                    message: "Target heading: 'range' mode requires both lo_deg and hi_deg" });
            } else if (Number(h.lo_deg) > Number(h.hi_deg)) {
                errors.push({ id: "heading-range-inverted", field: "target_heading",
                    message: `Target heading: lo_deg (${h.lo_deg}) must be ≤ hi_deg (${h.hi_deg})` });
            }
        }
    }
    if (vd.geometry_envelope) {
        for (const [axName, label] of [
            ["range_m", "Range"], ["bearing_deg", "Bearing"],
            ["altitude_m", "Altitude"], ["approach_vector_deg", "Approach vector"],
        ]) {
            const r = vd.geometry_envelope[axName];
            if (r && Number(r.lo) > Number(r.hi)) {
                errors.push({ id: `polar-${axName}-inverted`, field: `geometry_envelope.${axName}`,
                    message: `Envelope ${label}: minimum (${r.lo}) must be ≤ maximum (${r.hi})` });
            }
        }
    }

    return { errors, warnings, autoFixHints };
}

// ---------------------------------------------------------------------------
// Auto-fix recommender — called by UI controlled handlers.
// Maps a (gain_search, samplingKind) toggle event to the new state that
// preserves the invariant `gain_search ⇔ samplingKind === 'ga'`.
// ---------------------------------------------------------------------------
export function reconcileGaInvariants({
    gainSearch, samplerKind, gaGoal,
    changed, // one of "gainSearch" | "samplerKind" | "gaGoal"
}) {
    let next = { gainSearch, samplerKind, gaGoal };
    const hints = [];
    if (changed === "gainSearch") {
        if (gainSearch && samplerKind !== "ga") {
            next.samplerKind = "ga";
            hints.push("Switched sampling strategy to GA — required by guidance gain search.");
        } else if (!gainSearch && samplerKind === "ga") {
            next.samplerKind = "lhs";
            next.gaGoal = false;
            hints.push("Switched sampling back to LHS and disabled GA convergence goal — guidance gain search was turned off.");
        }
    } else if (changed === "samplerKind") {
        if (samplerKind === "ga" && !gainSearch) {
            next.gainSearch = true;
            hints.push("Enabled guidance gain search — required by GA sampling strategy.");
        } else if (samplerKind !== "ga" && gainSearch) {
            next.gainSearch = false;
            next.gaGoal = false;
            hints.push("Disabled guidance gain search and GA convergence goal — sampling strategy is no longer GA.");
        }
    } else if (changed === "gaGoal") {
        if (gaGoal && samplerKind !== "ga") {
            next.samplerKind = "ga";
            next.gainSearch = true;
            hints.push("Switched sampling to GA + enabled guidance gain search — required by GA convergence goal.");
        }
    }
    return { ...next, hints };
}
